import { memo, useState } from "react";
import { Container, Grid2, IconButton, InputAdornment, Link, TextField, Typography, FormControlLabel, Checkbox, FormHelperText, Box } from "@mui/material";
import ArrowButton from "@/ui-kit/ArrowButton";
import { icon } from "@/app/style/icons";
import { LocalStorage } from "@/types/enums";
import { LOGIN } from "@auth/graphql/mutations/login";
import { ApolloError, useMutation } from "@apollo/client";
import useAuthActions from "@auth/hooks/useAuthActions";
import { LoginValidationData, validateLoginCredentials } from "@/utils/validateCredentials";
import { useDispatch } from "react-redux";
import { handleErrorWithNotification } from "@/utils/errorHandling";
import { mapAuthError } from "@/utils/authErrorMapping";
import { AuthPayload } from "@/app/graphql/codegen";
import { RouteTo } from "@/app/routing/RouteMap";

interface FormErrors {
    email?: string;
    password?: string;
    submit?: string;
}

const LoginPage = memo(() => {
    const { login } = useAuthActions();
    const dispatch = useDispatch();
    const [email, setEmail] = useState(() => localStorage.getItem(LocalStorage.REMEMBERED_EMAIL) || "");
    const [password, setPassword] = useState("");
    const [rememberMe, setRememberMe] = useState(() => !!localStorage.getItem(LocalStorage.REMEMBERED_EMAIL));
    const [showPassword, setShowPassword] = useState(false);
    const [errors, setErrors] = useState<FormErrors>({});

    const [loginMutation, { loading }] = useMutation(LOGIN);

    // Bei Bedarf verschiedene loading states kombinieren
    const isLoading = loading;

    const validateForm = (): boolean => {
        const credentials: LoginValidationData = { email, password };
        const newErrors = validateLoginCredentials(credentials);
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleLogin = async () => {
        if (!validateForm()) return;

        loginMutation({
            variables: { email, password },
            onError: (error: ApolloError) => {
                const fieldErr = mapAuthError(error);
                // Mark fields that need attention
                setErrors((prev) => ({ ...prev, ...fieldErr }));

                // Toast notification – use submit-level message if we have one, else generic
                handleErrorWithNotification(
                    error,
                    dispatch,
                    "Fehler bei der Anmeldung",
                    fieldErr.submit || fieldErr.password || "Anmeldung fehlgeschlagen. Bitte überprüfen Sie Ihre Zugangsdaten."
                );
            },
            onCompleted: (data) => {
                const authPayload: AuthPayload = data.login;
                console.log(authPayload);

                // Handle remember me
                if (rememberMe) {
                    localStorage.setItem(LocalStorage.REMEMBERED_EMAIL, email);
                } else {
                    localStorage.removeItem(LocalStorage.REMEMBERED_EMAIL);
                }

                login(authPayload); // navigiert automatisch auf die Dashboard-Seite
            },
        });
    };

    const handleKeyDown = (event: React.KeyboardEvent) => {
        if (event.key === "Enter") {
            handleLogin();
        }
    };

    return (
        <Container maxWidth="xs">
            <Grid2 container size={12} spacing={3} justifyContent="center">
                <Grid2 size={12} mb={1} sx={{ textAlign: "center" }}>
                    <Typography variant="h2">Login</Typography>
                </Grid2>

                <Grid2 container size={12} spacing={2}>
                    <Grid2 size={12}>
                        <TextField
                            fullWidth
                            placeholder="E-Mail"
                            type="email"
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                setErrors((prev) => ({ ...prev, email: undefined, submit: undefined }));
                            }}
                            error={!!errors.email}
                            helperText={errors.email}
                            disabled={isLoading}
                            onKeyDown={handleKeyDown}
                        />
                    </Grid2>

                    <Grid2 size={12}>
                        <TextField
                            fullWidth
                            placeholder="Passwort"
                            type={showPassword ? "text" : "password"}
                            value={password}
                            onChange={(e) => {
                                setPassword(e.target.value);
                                setErrors((prev) => ({ ...prev, password: undefined, submit: undefined }));
                            }}
                            error={!!errors.password}
                            helperText={errors.password}
                            disabled={isLoading}
                            onKeyDown={handleKeyDown}
                            InputProps={{
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <IconButton onClick={() => setShowPassword(!showPassword)} edge="end" disabled={isLoading}>
                                            {showPassword ? (
                                                <icon.hidePassword sx={{ fontSize: 20, color: "semantic.icon.functional" }} />
                                            ) : (
                                                <icon.showPassword sx={{ fontSize: 20, color: "semantic.icon.functional" }} />
                                            )}
                                        </IconButton>
                                    </InputAdornment>
                                ),
                            }}
                        />
                    </Grid2>

                    <Grid2 size={12}
                            sx={{ ml: 2 }}>
                        <FormControlLabel
                            control={<Checkbox checked={rememberMe} onChange={(e) => setRememberMe(e.target.checked)} disabled={isLoading} />}
                            label="Angemeldet bleiben"
                        />
                        <FormHelperText>Ihre E-Mail-Adresse wird lokal gespeichert</FormHelperText>
                    </Grid2>

                    {errors.submit && (
                        <Grid2 sx={{ xs: 12 }}>
                            <Box sx={{ color: "semantic.notification.error", mt: 1 }}>{errors.submit}</Box>
                        </Grid2>
                    )}
                </Grid2>

                {/* Center the helper links below the form */}
                <Grid2 size={12} sx={{ display: "flex", justifyContent: "center", px: 0 }}>
                    <Box sx={{ display: "flex", gap: 2, width: "100%", maxWidth: 360 }}>
                         <Link href={RouteTo.FORGOT_PASSWORD} variant="nospace" sx={{ flex: 1, textAlign: "center" }}>
                             Passwort vergessen?
                         </Link>
                         <Link href={RouteTo.ONBOARDING} variant="nospace" sx={{ flex: 1, textAlign: "center" }}>
                             Registrieren
                         </Link>
                    </Box>
                </Grid2>

                <Grid2 size={12} sx={{ display: "flex", justifyContent: "center" }}>
                    <ArrowButton onClick={handleLogin} disabled={isLoading}>
                        {isLoading ? "Anmeldung..." : "Login"}
                    </ArrowButton>
                </Grid2>
            </Grid2>
        </Container>
    );
});

export default LoginPage;
